"use client";

import React, { useState } from 'react';
import { Sidebar, type NavItem } from './Sidebar';
import { Topbar } from './Topbar';
import type { Tenant } from '@vm/theme';

interface DashboardShellProps {
  children: React.ReactNode;
  tenant: Tenant;
  title: string;
  subtitle?: string;
  user?: {
    name?: string;
    email?: string;
    avatar?: string;
    role?: 'user' | 'admin';
    tier?: string;
  };
  navItems?: NavItem[];
  onCreateNew?: () => void;
  onUpgrade?: () => void;
  showCreateButton?: boolean;
  showUpgradeCard?: boolean;
  createButtonLabel?: string;
  /** Custom background class (defaults to slate gradient) */
  bgClass?: string;
}

export function DashboardShell({
  children,
  tenant,
  title,
  subtitle,
  user,
  navItems,
  onCreateNew,
  onUpgrade,
  showCreateButton = true,
  showUpgradeCard = true,
  createButtonLabel = 'Create New',
  bgClass = 'bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900',
}: DashboardShellProps) {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  // Wrap callbacks to close mobile menu first
  const handleCreateNew = () => {
    closeMobileMenu();
    onCreateNew?.();
  };

  const handleUpgrade = () => {
    closeMobileMenu();
    onUpgrade?.();
  };

  return (
    <div className={`min-h-screen ${bgClass} text-white font-inter`}>
      <div className="flex min-h-screen">
        {/* Sidebar - Hidden on mobile, visible on desktop */}
        <div className={`${isMobileMenuOpen ? 'fixed inset-y-0 left-0 z-40' : 'hidden'} lg:block lg:w-64 lg:flex-shrink-0`}>
          <Sidebar
            tenant={tenant}
            user={user}
            navItems={navItems}
            onCreateNew={handleCreateNew}
            onUpgrade={handleUpgrade}
            showCreateButton={showCreateButton}
            showUpgradeCard={showUpgradeCard}
            createButtonLabel={createButtonLabel}
          />
        </div>

        {/* Overlay for mobile */}
        {isMobileMenuOpen && (
          <div 
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-30 lg:hidden"
            onClick={toggleMobileMenu}
          />
        )}

        {/* Main Content */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Top bar */}
          <Topbar
            title={title}
            subtitle={subtitle}
            onMobileMenuToggle={toggleMobileMenu}
            user={user}
            showUpgradeButton={showUpgradeCard}
          />

          {/* Main Content */}
          <section className="flex-1 overflow-y-auto px-4 py-8 sm:px-6 lg:px-10 lg:py-10 space-y-6 lg:space-y-8">
            {children}
          </section>
        </div>
      </div>
    </div>
  );
}
